import { requestIdHeaderName } from '@sit/core';
import axios, { AxiosError, AxiosHeaders, } from 'axios';
import mem from 'mem';
import { v4 as uuidv4 } from 'uuid';
import { getRefreshToken, getToken, onLogout, setToken } from './auth.js';
import { isResponse, parseResponseError } from './error.js';
export const axiosInstance = axios.create();
/**
 * This function be overwritten at run-time by each client.
 */
export function setBaseUrl(url) {
    axiosInstance.defaults.baseURL = url;
}
const isAuthUrl = (url) => {
    if (!url)
        return false;
    return url.includes('auth') || url.includes('users/logout');
};
async function attemptRetry(error) {
    if (!isResponse(error.response) ||
        error.response.status !== 401 ||
        error.code === AxiosError.ERR_NETWORK) {
        throw error;
    }
    const response = error.response;
    const refreshToken = getRefreshToken();
    const { status, config } = response;
    const shouldRetry = !config.noRetry &&
        status === 401 &&
        !config.retried &&
        response.data !== 'Account has been deactivated by admin';
    if (shouldRetry) {
        config.retried = true;
        const token = await memoizedNewToken(refreshToken || null);
        if (token) {
            config.headers = buildHeaders(config.headers, token);
            setToken(token);
            return await axios(config);
        }
    }
    if (!isAuthUrl(config.url)) {
        onLogout?.();
    }
    throw error;
}
const interceptBeforeRetryHandlers = [];
const interceptAfterErrorHandlers = [];
async function interceptErrorResponse(error) {
    // Meta is used to pass data between handlers.
    // It is a mutable reference.
    const meta = new Map();
    try {
        for (const fn of interceptBeforeRetryHandlers) {
            const result = await fn(error, meta);
            if (result) {
                return result;
            }
        }
        return await attemptRetry(error);
    }
    catch (apiError) {
        if (apiError instanceof AxiosError) {
            const normalizedError = parseResponseError(apiError);
            for (const fn of interceptAfterErrorHandlers) {
                const result = await fn(normalizedError, meta);
                if (result) {
                    return result;
                }
            }
            throw normalizedError;
        }
        throw apiError;
    }
}
export function addBeforeRetryErrorInterceptor(fn) {
    interceptBeforeRetryHandlers.push(fn);
}
export function addAfterRetryErrorInterceptor(fn) {
    interceptAfterErrorHandlers.push(fn);
}
const requestNewToken = async (refreshToken) => {
    if (!refreshToken)
        return null;
    try {
        const { data } = await axiosInstance.post('/pub/token', {
            refreshToken,
        });
        return data.token;
    }
    catch {
        return null;
    }
};
const memoizedNewToken = mem(requestNewToken, { maxAge: 1000 * 60 * 60 * 4 }); // four hours
export const buildHeaders = (additionalHeaders, token) => {
    const headers = new AxiosHeaders();
    headers.set('Accept', 'application/json');
    headers.set('Content-Type', 'application/json');
    headers.set(requestIdHeaderName, uuidv4());
    if (additionalHeaders) {
        Object.entries(additionalHeaders).forEach(([key, value]) => {
            headers.set(key, value);
        });
    }
    if (token) {
        headers.set('Authorization', `JWT ${token}`);
    }
    return headers;
};
const logoutUserRequest = async (newToken, refreshToken) => {
    const token = newToken ?? getToken();
    const refresh = refreshToken ?? getRefreshToken();
    const headers = buildHeaders({}, token);
    return await axiosInstance.post('/users/logout', refresh
        ? {
            refreshToken: refresh,
        }
        : {}, { headers });
};
export const logoutUser = mem(logoutUserRequest, { maxAge: 5000 });
// Side-effect
axiosInstance.interceptors.response.use((response) => response, interceptErrorResponse);
//# sourceMappingURL=axios-instance.js.map